module Meevo.Components.Common {

	export enum DeviceType {
		Epson = 1,
		Star = 2,
	}

	export interface IPrintSettings {
		ipAddress: string;
		port: number;
		id: string;
		deviceName: string;
		ribbonSize: number;
		deviceType?: DeviceType;
		isTrustEnabled?: boolean;
	}

	export class PrintProxyService implements IPrintProxyService {

		windowPrintProxy = null;
		windowPrintProxyFailed = false;
		framePrintProxy = null;
		framePrintProxyId = 'printProxyFrame';
		isTrustEnabled = null;
		responseMap = {};

		constructor(
			private $window,
			private $timeout,
			private $q: angular.IQService,
			private $translate: angular.translate.ITranslateService,
			private appSettings: Meevo.Components.Common.IAppSettings) {
			this.$window.addEventListener('message', (event) => this.responseEventHandler.call(this, event), false);
		}

		responseEventHandler(event: any) {
			const response = event.data;

			if (response.source !== "Meevo2PrintProxy") {
				return;
			}

			event.stopPropagation();

			if (this.responseMap.hasOwnProperty(response.requestId)) {
				const responseCallback = this.responseMap[response.requestId];
				if (responseCallback) {
					responseCallback.call(this, response);
					delete this.responseMap[response.requestId];
				}
			}
		}

        private printProxyQueueInterval: number = 250;  /* time between retries = queue interval */
        private printProxyQueue: number = undefined;

        private ensurePrintQueueTimer() {
            if (this.printProxyQueue) {
                return;
            }

            this.printProxyQueue = window.setInterval(() => {
                this.processQueue().catch(() => {
                    // if processQueue fails, then likely the print proxy window is gone
                    window.clearInterval(this.printProxyQueue);
                    this.printProxyQueue = undefined;
                });
            }, this.printProxyQueueInterval);
        }

		initialize(printer: IPrintSettings): ng.IPromise<any> {
			this.ensurePrintQueueTimer();

			const task = this.$q.defer();

			const payload = {
				ipAddress: printer.ipAddress,
				port: printer.port,
				id: printer.id,
				deviceName: printer.deviceName,
				ribbonSize: printer.ribbonSize,
				deviceType: printer.deviceType,
				title: this.$translate.instant('MESSAGE_PRINTPROXY_TITLE'),
				message: this.$translate.instant('MESSAGE_PRINTPROXY_PRINTER_CONNECTION'),
				isTrustEnabled: printer.isTrustEnabled,
				poleDisplay: true,
			};

			this.postMessage('initialize', payload)
				.then((response) => {
					task.resolve(response);
				})
				.catch((response) => {
					task.reject(response);
				});

			return task.promise;
		}

		acknowledge(): ng.IPromise<any> {
			const task = this.$q.defer();

			if (this.printProxyAvailable()) {
				task.resolve();
			}

			this.postMessage('acknowledge', {})
				.then((response: any) => {
					if (response.payload.message === "hello_friend") {
						task.resolve();
					} else {
						task.reject();
					}
				})
				.catch(() => {
					task.reject();
				});

			return task.promise;
		}

		processQueue(): ng.IPromise<any> {
			const task = this.$q.defer();

			if (!this.printProxyAvailable()) {
				task.reject();
			}

			this.postMessage('processQueue', {})
				.then(() => {
					// processQueue doesn't send a response object with failure conditions
					task.resolve();
				})
				.catch(() => {
					task.reject();
				});

			return task.promise;
		}

		printProxyAvailable(): boolean {
			return (this.isTrustEnabled === true && this.framePrintProxy && $(`#${this.framePrintProxyId}`).length !== 0) ||
				   (this.isTrustEnabled === false && this.windowPrintProxy && !this.windowPrintProxy.closed);
		}

		postMessage(action: string, payload: any) {
			// if any of these conditions exists, then recreate the window
			// - window not created
			// - window created but closed
			// - window created but initialize message not received
			// - window created but using printer with different trust setting

			if (payload.isTrustEnabled !== undefined && payload.isTrustEnabled !== this.isTrustEnabled) {
				this.isTrustEnabled = payload.isTrustEnabled;
			}

			const task = this.$q.defer();

			if (!this.printProxyAvailable() || this.isTrustEnabled) {
				this.windowPrintProxyFailed = null;
			}

			if (this.windowPrintProxyFailed) {
				task.reject('windowPrintProxyFailed');
				return task.promise;
			}

			if (!this.isTrustEnabled && device.standalone()) {
				task.reject('standalone');
				return task.promise;
			}

			if (this.printProxyAvailable()) {
				return this.postMessageContinue(action, payload);
			}

			if (action === 'processQueue') {
				return task.promise;
			}

			const url = this.getUrl();

			if (url && url.length > 0) {
				const timeoutPromise = this.$timeout(() => {
					task.reject('popup_timeout');
				}, 5000);

				this.responseMap['PrintProxyLoaded'] = () => {
					this.$timeout.cancel(timeoutPromise);

					if (!this.isTrustEnabled) {
						this.$window.addEventListener('unload', () => {
							if (this.windowPrintProxy != null) {
								this.windowPrintProxy.close();
								this.windowPrintProxy = null;
							}
						});
					}

					task.resolve(this.postMessageContinue(action, payload));
				};

				if (this.isTrustEnabled) {
					let $frame = $(`#${this.framePrintProxyId}`);
					if ($frame.length === 0) {
						$frame = $(`<iframe id="${this.framePrintProxyId}" name="${this.framePrintProxyId}" src="${url}">`).hide();
						$frame.on('load', () => {
							this.framePrintProxy = this.$window.frames[this.framePrintProxyId];
						});
						$('body').prepend($frame);
					} else {
						this.framePrintProxy = this.$window.frames[this.framePrintProxyId];
					}
				} else {
					this.windowPrintProxy = this.$window.open(url, 'PrintProxyWindow', 'width=300,height=300,menubar=0,toolbar=0,status=0');
				}
			} else {
				console.error('PrintProxyUrl is not defined!');
				task.reject();
			}

			return task.promise;
		}

		private getUrl() {
			const printProxyUrl = this.$window['PrintProxyUrl'].replace('PrintProxy', 'assets');

			const url = this.isTrustEnabled
				? location.origin + printProxyUrl.substring(printProxyUrl.indexOf('/assets/'))
				: printProxyUrl;

			return url;
		}

		private postMessageContinue(action: string, payload: any) {
			const task = this.$q.defer();

			if (this.printProxyAvailable()) {
				const request = {
					requestId: Math.random().toString(36).substr(2, 16),
					source: 'Meevo2',
					origin: location.origin || `${location.protocol}//${location.host}`,
					action: action,
					payload: payload,
				};

				this.responseMap[request.requestId] = (response) => {
					if (response.action !== 'initialize') {
						const log = `[${response.requestId}][${response.action}] meevo.receiveMessage`;
						const logFunction = response.payload.success ? console.debug : console.error;
						logFunction.call(this, log, response.payload.status || '', response, request);
					}

					if (response.action === 'initialize' && response.request.payload.isUnexpectedHttpsUpgrade) {
						if (!this.isTrustEnabled && this.windowPrintProxy != null) {
							this.windowPrintProxyFailed = true;
						}

						task.reject(response.payload.message);

						return;
					}

					task.resolve(response);
				};

				switch (request.action) {
					case 'initialize':
					case 'processQueue':
						break;
					default:
						const log = `[${request.requestId}][${request.action}] meevo.postMessage`;
						console.debug(log, request);
						break;
				}

				const targetOrigin = this.getUrl();

				if (this.isTrustEnabled) {
					this.framePrintProxy.postMessage(request, targetOrigin)
				} else {
					this.windowPrintProxy.postMessage(request, targetOrigin);
					if (request.action === 'initialize') {
						request.payload.isUnexpectedHttpsUpgrade = true;
						request.payload.message = this.$translate.instant('MESSAGE_PRINTPROXY_FAILURE');
					}
  					this.windowPrintProxy.postMessage(request, targetOrigin.replace('http://', 'https://'));
				}
			} else {
				task.reject();
			}

			return task.promise;
		}

		static factory(): Function {
			const svc = (
				$window,
				$timeout,
				$q: angular.IQService,
				$translate: angular.translate.ITranslateService,
				appSettings: Meevo.Components.Common.IAppSettings,
			) => new PrintProxyService(
				$window,
				$timeout,
				$q,
				$translate,
				appSettings
			);

			svc.$inject = [
				"$window",
				"$timeout",
				"$q",
				"$translate",
				"meevo.AppSettings"
			];

			return svc;
		}

	}

	angular.module("meevo.components.common")
		.factory("meevo.components.common.PrintProxyService", PrintProxyService.factory());
}
